const Device = require('@/lib/Devices/Device');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { apiResponseToError } = require('@/lib/Api');
const { isString } = require('@/functions/is');

/**
 * @constructor
 *
 * @param $q
 * @param {AgentService} agentService
 * @param {ApiClient} apiClient
 * @param {AppConfig} appConfig
 * @param deviceResource
 * @param {Hydrator} hydrator
 * @param {PrinterAppPrintDriver} printerAppPrintDriver
 * @param {CurrentDevice} currentDevice
 */
const DeviceManager = function (
    $q,
    agentService,
    apiClient,
    appConfig,
    deviceResource,
    hydrator,
    printerAppPrintDriver,
    currentDevice
) {
    this.$q = $q;
    this.agentService = agentService;
    this.apiClient = apiClient;
    this.appConfig = appConfig;
    this.deviceResource = deviceResource;
    this.hydrator = hydrator;
    this.currentDevice = currentDevice;
    this.printerAppPrintDriver = printerAppPrintDriver;

    /**
     * @type {ConsoleLogger}
     */
    this.log = logFactory('DeviceManager');

    this.devices = [];
};

DeviceManager.prototype = {
    /**
     * Assign a device after it has been chosen from the device list.
     *
     * @param {number} deviceID
     *
     * @return {Promise<Device>}
     */
    assignDevice(deviceID) {
        return new Promise((resolve, reject) => {
            this.deviceResource.setGuid(
                {
                    deviceID,
                    // Later we could start generating our own recreatable guid on ProPoint.
                    // For now we omit this and the API generates a guid.
                    // guid: "some-string"
                },
                {},
                (response) => {
                    const responseDevice = this.hydrator.hydrate(response, new Device());
                    this.currentDevice.setDevice(responseDevice, false);

                    resolve(responseDevice);
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * @param {number} deviceID
     *
     * @return {Promise}
     */
    unassignDevice(deviceID) {
        return new Promise((resolve, reject) => {
            this.deviceResource.clearGuid(
                {
                    deviceID,
                },
                {},
                () => {
                    resolve();
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * Triggered when a user selects a device - calls the API to update the currently logged-in
     * user for a device.
     *
     * @param {number} deviceID
     * @param {number} currentUserID
     *
     * @return {Promise}
     */
    setCurrentUser(deviceID, currentUserID) {
        return new Promise((resolve, reject) => {
            this.deviceResource.setUser(
                {
                    deviceID,
                },
                {
                    currentUserID,
                },
                () => {
                    resolve();
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    refreshDevice() {
        // Try to get a fresh copy of the device data from the API then save
        let self = this;
        let deferred = self.$q.defer();

        if (!this.currentDevice.isSet()) {
            // There is no device set.
            // Small timeout so resolving the promise happens asynchronously after it has been returned
            // to the caller.
            setTimeout(function () {
                deferred.resolve(self.currentDevice.device);
            }, 1);
        } else if (self.currentDevice.getDeviceGuid()) {
            // Only make the request if we have a cached guid otherwise the request will fail
            self.deviceResource.refresh(
                {
                    // The deviceGuid that has been stored in the cache
                    // The API will test against this rather than the cookie
                    deviceGuid: self.currentDevice.getDeviceGuid(),
                },
                {},
                function (response) {
                    // Update our current device with the new info
                    let device = self.hydrator.hydrate(response, new Device());
                    self.currentDevice.setDevice(device, false);
                    deferred.resolve(self.currentDevice.device);
                },
                function (response) {
                    if (response.status >= 400 && response.status <= 499) {
                        // If the response was says the request for the device was 'bad', then clear it
                        self.currentDevice.clearDevice();
                    }

                    deferred.resolve(self.currentDevice.device);
                }
            );
        } else {
            // We won't have a cached guid if this is a new device or the browser cache has been cleared
            self.currentDevice.clearDevice();
            deferred.resolve(self.currentDevice.device);
        }

        return deferred.promise;
    },

    /**
     * @return {Promise<Device[]>}
     */
    loadDevices() {
        return new Promise((resolve, reject) => {
            this.deviceResource.query(
                (devices) => {
                    // Resolve with a list of hydrated Devices.
                    resolve(
                        this.hydrator.hydrateArray(
                            devices,
                            () => new Device()
                        )
                    );
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    sendDeviceInfo() {
        let deviceID = this.currentDevice.getDeviceId();
        if (!deviceID) {
            throw new Error('No deviceID to send info for.');
        }

        this.getDeviceInfo()
            .then((deviceInfo) => {
                console.log('[DeviceManager]', 'Sending device info to API', deviceInfo);
                this.deviceResource.setDeviceInfo(
                    {
                        deviceID,
                    },
                    deviceInfo
                );
            });
    },

    /**
     * Returns device info supplied by the browser.
     *
     * @return {object}
     */
    getDefaultDeviceInfo() {
        let systemTimezoneName = null;
        try {
            systemTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            // Just in case the above is not supported.
        }

        return {
            systemTime: (new Date()).toISOString(),
            systemTimezoneName,
            eposVersion: this.appConfig.eposCommit,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            screenWidth: typeof window !== 'undefined' ? window.screen.width : null,
            screenHeight: typeof window !== 'undefined' ? window.screen.height : null,
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : null,
            viewportHeight: typeof window !== 'undefined' ? window.innerHeight : null,
        };
    },

    /**
     * Returns all device info including that which is supplied by the agent.
     *
     * @return {Promise<{}>}
     */
    getDeviceInfo() {
        let agentInfo = {};
        let printerAppInfo = {};

        let promises = [];

        promises.push(
            this.agentService.getServerInfo()
                .then((response) => {
                    agentInfo = response;
                })
                .catch((err) => {
                    console.debug(
                        'Couldn\'t get device info from ProPoint Agent. If ProPoint Agent is not installed this is not an issue.',
                        err
                    );
                    // Okay, save what we can anyway.
                    // Having this catch resolves any rejections and the promise chain continues as resolved.
                })
        );

        promises.push(
            this.printerAppPrintDriver.getAppInfo()
                .then((response) => {
                    printerAppInfo = response;
                })
                .catch((err) => {
                    // The wording of this log has been changed so that it doesn't scare support people
                    // looking in the console.
                    console.debug(
                        'ProPoint Printer app is not available. If ProPoint Printer is not installed this is not an issue.',
                        err
                    );
                })
        );

        return Promise.all(promises)
            .then(() => Object.assign(
                {},
                this.getDefaultDeviceInfo(),
                agentInfo,
                printerAppInfo
            ));
    },

    setPaymentTerminalProxyInfo: function setPaymentTerminalProxyInfo(data) {
        // TODO: Remove
        let currentDevice = this.currentDevice.device;
        if (currentDevice && currentDevice.ID) {
            this.deviceResource.setPaymentTerminalProxyInfo(
                {
                    deviceID: this.currentDevice.getDeviceId(),
                },
                {
                    info: data,
                }
            );
        }
    },

    /**
     * @param {string} guid
     *
     * @return {Promise<Device>}
     */
    queryDeviceByGuid(guid) {
        return new Promise((resolve, reject) => {
            this.deviceResource.query(
                {
                    guid,
                },
                (response) => {
                    if (response && response.length > 0 && response[0].deviceID) {
                        let device = this.hydrator.hydrate(response[0], new Device());
                        resolve(device);
                    } else {
                        reject();
                    }
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    },

    /**
     * @return {Promise<Device|null>}
     */
    async loadCurrentDeviceFromGuidOnDisk() {
        let guidSavedOnDisk;
        let device;

        try {
            // Try and load a device by guid saved on disk.
            guidSavedOnDisk = await this.currentDevice.readDeviceGuidFromDisk();
        } catch (e) {
            // Failed to read guid from disk. Move on.
            return null;
        }

        if (!guidSavedOnDisk) {
            return null;
        }

        // Found something! Send it to the API and check it is still valid.
        this.log.log('Found saved guid on disk.', guidSavedOnDisk);

        try {
            device = await this.queryDeviceByGuid(guidSavedOnDisk);
        } catch (e) {
            return null;
        }

        this.log.log('Found device matching guid on disk. Setting as current device.', device);

        this.currentDevice.setDevice(device, false);

        return device;
    },

    async updateDevice(deviceID, data) {
        if (data.hasOwnProperty('paymentTerminalConfig') && !isString(data.paymentTerminalConfig)) {
            // This should be sent as a JSON string.
            data.paymentTerminalConfig = JSON.stringify(data.paymentTerminalConfig);
        }

        return this.apiClient
            .request(
                'PATCH',
                `devices/${deviceID}`,
                data
            )
            .then((response) => {
                console.log('response', response);
            });
    },
};

module.exports = DeviceManager;
